// Parser fuer per Drag&Drop abgelegte .eml- und .msg-Dateien.
// Liefert ein normalisiertes Objekt, das direkt in IIncomingEmailRequest-
// Felder bzw. in den RecordIncomingDialog vorbefuellt werden kann.

import PostalMime from 'postal-mime';
import MsgReader from '@kenjiuno/msgreader';

export interface ParsedAttachment {
  fileName: string;
  /** base64 ohne data:-Prefix */
  data: string;
  mimeType?: string;
}

export interface ParsedEmail {
  from: string;
  to: string;
  cc?: string;
  subject: string;
  body: string;
  bodyContentType: 'html' | 'text';
  receivedAt?: string; // ISO-String
  externalMessageId?: string;
  attachments: ParsedAttachment[];
  /** Hinweis, wenn beim Parsing etwas auffaellig war (z. B. Exchange-LDAP
   *  Sender, verschluesselter Inhalt). Wird im UI angezeigt. */
  warning?: string;
  /** Welches Format erkannt wurde. */
  format: 'eml' | 'msg';
}

const u8ToBase64 = (bytes: Uint8Array): string => {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, i + chunk);
    // Spread auf Uint8Array braucht downlevelIteration — apply ist kompatibel.
    bin += String.fromCharCode.apply(null, Array.from(slice));
  }
  return btoa(bin);
};

const stringToBase64 = (str: string): string => {
  const enc = new TextEncoder().encode(str);
  return u8ToBase64(enc);
};

const formatAddress = (
  addr?: { name?: string; address?: string } | null
): string => {
  if (!addr) return '';
  const name = (addr.name || '').trim();
  const email = (addr.address || '').trim();
  if (name && email) return `${name} <${email}>`;
  return email || name;
};

const formatAddressList = (
  list?: Array<{ name?: string; address?: string } | null> | null
): string => (list ?? []).map(formatAddress).filter(Boolean).join(', ');

// ---------------------------------------------------------------------------
// .eml (RFC 822) via postal-mime
// ---------------------------------------------------------------------------

const parseEml = async (file: File): Promise<ParsedEmail> => {
  const buf = await file.arrayBuffer();
  const parser = new PostalMime();
  const mail = await parser.parse(buf);

  const html = (mail.html || '').trim();
  const text = (mail.text || '').trim();
  const hasHtml = !!html;

  const attachments: ParsedAttachment[] = [];
  for (const a of mail.attachments ?? []) {
    if (!a.content) continue;
    let data: string;
    if (typeof a.content === 'string') {
      data = a.encoding === 'base64' ? a.content : stringToBase64(a.content);
    } else if (a.content instanceof Uint8Array) {
      data = u8ToBase64(a.content);
    } else {
      data = u8ToBase64(new Uint8Array(a.content));
    }
    attachments.push({
      fileName: a.filename || 'attachment.bin',
      data,
      mimeType: a.mimeType,
    });
  }

  // 'from' aus postal-mime ist ein einzelnes Address-Objekt (oder undef).
  const from = formatAddress(
    mail.from && 'address' in mail.from ? mail.from : undefined
  );

  const toList = (mail.to ?? []).flatMap(addr =>
    addr && 'address' in addr ? [addr] : []
  );
  const ccList = (mail.cc ?? []).flatMap(addr =>
    addr && 'address' in addr ? [addr] : []
  );

  return {
    from,
    to: formatAddressList(toList),
    cc: ccList.length > 0 ? formatAddressList(ccList) : undefined,
    subject: mail.subject || '',
    body: hasHtml ? html : text,
    bodyContentType: hasHtml ? 'html' : 'text',
    receivedAt: mail.date ? new Date(mail.date).toISOString() : undefined,
    externalMessageId: mail.messageId || undefined,
    attachments,
    format: 'eml',
  };
};

// ---------------------------------------------------------------------------
// .msg (Outlook Compound File) via @kenjiuno/msgreader
// ---------------------------------------------------------------------------

interface MsgRecipient {
  name?: string;
  email?: string;
  recipType?: 'to' | 'cc' | 'bcc' | string;
}

const isExchangeAddress = (s?: string): boolean =>
  !!s && /^\/o=|^\/O=/.test(s.trim());

const formatMsgRecipient = (r: MsgRecipient): string => {
  const name = (r.name || '').trim();
  const email = (r.email || '').trim();
  if (name && email && name !== email) return `${name} <${email}>`;
  return email || name;
};

const parseMsg = async (file: File): Promise<ParsedEmail> => {
  const buf = await file.arrayBuffer();
  const reader = new MsgReader(buf);
  const data = reader.getFileData();

  if (data.error) {
    throw new Error(`MSG-Parsing fehlgeschlagen: ${data.error}`);
  }

  const subject = (data.subject || '').toString();

  // Sender: bevorzugt SMTP, sonst Exchange-LDAP-Fallback (mit Hinweis).
  let warning: string | undefined;
  let senderEmail = (data.senderEmail || '').toString().trim();
  const senderName = (data.senderName || '').toString().trim();
  if (isExchangeAddress(senderEmail)) {
    warning =
      'Absender liegt nur im Exchange-Format vor — bitte SMTP-Adresse manuell ergaenzen.';
    senderEmail = '';
  }
  let from = '';
  if (senderName && senderEmail) from = `${senderName} <${senderEmail}>`;
  else from = senderEmail || senderName;

  const recipients = (data.recipients as MsgRecipient[] | undefined) ?? [];
  const to = recipients
    .filter(r => (r.recipType || 'to').toLowerCase() === 'to')
    .map(formatMsgRecipient)
    .filter(Boolean)
    .join(', ');
  const cc = recipients
    .filter(r => (r.recipType || '').toLowerCase() === 'cc')
    .map(formatMsgRecipient)
    .filter(Boolean)
    .join(', ');

  // Body bevorzugt als HTML — bodyHtml oder html (Uint8Array)
  let body = '';
  let bodyContentType: 'html' | 'text' = 'text';
  if (data.bodyHtml && data.bodyHtml.trim()) {
    body = data.bodyHtml;
    bodyContentType = 'html';
  } else if (data.html && data.html.length > 0) {
    body = new TextDecoder('utf-8').decode(data.html);
    bodyContentType = 'html';
  } else if (data.body) {
    body = data.body;
    bodyContentType = 'text';
  }

  // Datum: messageDeliveryTime (eingegangen) oder clientSubmitTime (gesendet)
  const rawDate =
    (data as { messageDeliveryTime?: string; clientSubmitTime?: string })
      .messageDeliveryTime ||
    (data as { clientSubmitTime?: string }).clientSubmitTime;
  let receivedAt: string | undefined;
  if (rawDate) {
    const parsed = new Date(rawDate);
    if (!Number.isNaN(parsed.getTime())) {
      receivedAt = parsed.toISOString();
    }
  }

  // Anhaenge: getAttachment(i) liefert content (Uint8Array) + fileName
  const attachments: ParsedAttachment[] = [];
  for (const att of data.attachments ?? []) {
    try {
      const aData = reader.getAttachment(att);
      if (aData && aData.content && aData.content.length > 0) {
        attachments.push({
          fileName: aData.fileName || 'attachment.bin',
          data: u8ToBase64(aData.content),
        });
      }
    } catch {
      // Einzelne Attachments koennen verschluesselt/unlesbar sein — ignorieren.
    }
  }

  return {
    from,
    to,
    cc: cc || undefined,
    subject,
    body,
    bodyContentType,
    receivedAt,
    attachments,
    warning,
    format: 'msg',
  };
};

// ---------------------------------------------------------------------------
// Public entry — Format-Detection per Endung
// ---------------------------------------------------------------------------

export const parseEmailFile = async (file: File): Promise<ParsedEmail> => {
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.msg')) return parseMsg(file);
  if (name.endsWith('.eml') || name.endsWith('.mht')) return parseEml(file);
  // Fallback: per Sniff — Outlook .msg beginnt mit dem CFBF-Magic D0CF11E0,
  // .eml ist Text. Wir nehmen .eml als Default, falls Endung fehlt.
  const head = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  const isCfbf =
    head[0] === 0xd0 &&
    head[1] === 0xcf &&
    head[2] === 0x11 &&
    head[3] === 0xe0;
  return isCfbf ? parseMsg(file) : parseEml(file);
};

export const isParseableEmail = (file: File): boolean => {
  const name = (file.name || '').toLowerCase();
  return (
    name.endsWith('.eml') || name.endsWith('.msg') || name.endsWith('.mht')
  );
};
